// src/pages/admin/AdminPanel.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore, collection, addDoc, deleteDoc, doc, query,
  orderBy, getDoc, onSnapshot, updateDoc, serverTimestamp, limit,
  setDoc, Timestamp
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import app from "../../firebase";
import { Helmet } from "react-helmet-async";
import { useToast } from "../../context/ToastContext";
import PartnerApplicationsPanel from "./PartnerApplicationsPanel";
import BrokerApplicationsPanel from "./BrokerApplicationsPanel";
import BrokerContentPanel from "./BrokerContentPanel";
import { FiUsers, FiInbox, FiActivity, FiMessageSquare, FiSettings, FiInfo, FiPhoneCall, FiUserCheck, FiBookOpen } from "react-icons/fi";

// ---------- Brand tokens ----------
const brand = {
  bg: "bg-[#f5f8fc]",
  text: "text-[#0f2437]",
  btnPrimary:
    "bg-[#002b5c] text-white hover:bg-[#082f6d] focus:ring-2 focus:ring-[#002b5c]/30",
  btnGhost:
    "bg-white border border-gray-200 hover:bg-gray-50",
  btnInverted:
    "bg-white text-[#002b5c] hover:bg-white/90 border border-white/20 shadow-sm",
  card:
    "bg-white border border-gray-200 shadow-sm rounded-2xl",
  input:
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-[#002b5c]/25 focus:border-[#002b5c]/40",
  chip: "px-3 py-1 rounded-lg bg-gray-100 text-gray-700",
};

// ---------- Sidebar link ----------
function SidebarLink({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2 mb-1 transition
        ${active ? "bg-[#002b5c] text-white" : "hover:bg-gray-100 text-gray-800"}`}
    >
      <Icon className="text-lg shrink-0" />
      <span>{children}</span>
    </button>
  );
}

// ---------- Avatar Uploader ----------
function AvatarUploader({ file, preview, onFileChange, label = "Upload Photo" }) {
  const inputRef = useRef(null);
  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <div className="w-20 h-20 rounded-full border border-gray-200 overflow-hidden shadow-sm bg-gray-50">
          {preview ? (
            <img src={preview} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center text-xs text-gray-400">
              No Photo
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="absolute -bottom-2 -right-2 rounded-full px-3 py-1 text-xs shadow bg-white border hover:bg-gray-50"
          title={label}
        >
          Edit
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onFileChange(e.target.files?.[0] || null)}
        />
      </div>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`h-10 px-4 rounded-full ${brand.btnGhost}`}
        >
          {label}
        </button>
        {preview && (
          <button
            type="button"
            onClick={() => onFileChange(null)}
            className="h-9 px-3 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm"
          >
            Remove
          </button>
        )}
        <div className="text-[11px] text-gray-500">JPG/PNG, ≤5MB</div>
      </div>
    </div>
  );
}

// ---------- helpers ----------
const sanitizeZip = (v) => (v || "").replace(/\D/g, "").slice(0, 5);
const isValidUSZip = (zip) => /^\d{5}$/.test(zip);
const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(email || ""));

async function fetchCityState(zip) {
  const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
  if (!res.ok) throw new Error("Invalid ZIP");
  const data = await res.json();
  const place = data?.places?.[0];
  return {
    city: place?.["place name"] || "",
    state: place?.["state abbreviation"] || "",
  };
}

// ---------- Settings: Announcement Bar Editor ----------
function toInputValue(tsLike) {
  if (!tsLike) return "";
  const d = tsLike?.toDate ? tsLike.toDate() : new Date(tsLike);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromInputValue(v) {
  if (!v) return null;
  const ms = new Date(v).getTime();
  return isNaN(ms) ? null : Timestamp.fromMillis(ms);
}

function AnnouncementBarCard() {
  const db = getFirestore(app);
  const { addToast } = useToast();
  const notifySuccess = (m) => addToast?.(m, "success");
  const notifyError = (m) => addToast?.(m, "error");

  const [form, setForm] = React.useState({
    enabled: true,
    message: "",
    linkUrl: "",
    linkLabel: "",
    dismissible: true,
    startAt: "",
    endAt: "",
  });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    const ref = doc(db, "site_settings", "announcementBar");
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setForm({
          enabled: !!d.enabled,
          message: d.message || "",
          linkUrl: d.linkUrl || "",
          linkLabel: d.linkLabel || "",
          dismissible: !!d.dismissible,
          startAt: toInputValue(d.startAt),
          endAt: toInputValue(d.endAt),
        });
      }
    });
    return () => unsub();
  }, []);

  const updateField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      setSaving(true);
      const ref = doc(db, "site_settings", "announcementBar");
      await setDoc(
        ref,
        {
          enabled: !!form.enabled,
          message: String(form.message || "").trim(),
          linkUrl: String(form.linkUrl || "").trim(),
          linkLabel: String(form.linkLabel || "").trim(),
          dismissible: !!form.dismissible,
          startAt: fromInputValue(form.startAt),
          endAt: fromInputValue(form.endAt),
          updatedAt: Timestamp.now(), // forces re-show for users who dismissed
        },
        { merge: true }
      );
      notifySuccess?.("Announcement saved.");
    } catch (e) {
      console.error(e);
      notifyError?.("Failed to save announcement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${brand.card} p-5`}>
      <h2 className="text-xl font-semibold mb-1">Announcements Bar</h2>
      <p className="text-sm text-gray-600 mb-4">
        Controls the small message shown in the red strip at the top of the site, next to the phone number.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={form.enabled}
            onChange={(e) => updateField("enabled", e.target.checked)}
          />
          <span className="font-medium">Enabled</span>
        </label>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={form.dismissible}
            onChange={(e) => updateField("dismissible", e.target.checked)}
          />
          <span className="font-medium">Allow users to dismiss</span>
        </label>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Message</label>
          <textarea
            rows={3}
            className={brand.input}
            placeholder="e.g., Annual Enrollment is Oct 15 – Dec 7. Book your review today."
            value={form.message}
            onChange={(e) => updateField("message", e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Link label (optional)</label>
          <input
            className={brand.input}
            placeholder="Learn more"
            value={form.linkLabel}
            onChange={(e) => updateField("linkLabel", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Link URL (internal or external)</label>
          <input
            className={brand.input}
            placeholder="/help or https://example.com"
            value={form.linkUrl}
            onChange={(e) => updateField("linkUrl", e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Use a route like <code>/help</code> for internal pages, or a full URL for external links.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Start time (optional)</label>
          <input
            type="datetime-local"
            className={brand.input}
            value={form.startAt}
            onChange={(e) => updateField("startAt", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">End time (optional)</label>
          <input
            type="datetime-local"
            className={brand.input}
            value={form.endAt}
            onChange={(e) => updateField("endAt", e.target.value)}
          />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className={`rounded-xl px-4 py-2 ${brand.btnPrimary} disabled:opacity-60`}
        >
          {saving ? "Saving…" : "Save Announcement"}
        </button>
        <span className="text-xs text-gray-500">
          Saving updates the timestamp so previously dismissed banners re-appear.
        </span>
      </div>

      {/* Live-ish preview */}
      <div className="mt-6">
        <div className="bg-[#e31c3d] text-white rounded-lg px-4 py-3">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2">
            <div className="text-sm font-medium">
              <FiPhoneCall className="inline-block mr-2" />
              Questions? Call us now{" "}
              <span className="underline font-semibold">+1 (914) 358-4068</span>
            </div>
            {form.enabled && (
              <div className="text-xs md:text-sm bg-white/10 rounded-md px-3 py-2 flex items-center gap-2">
                <FiInfo />
                <span className="font-semibold uppercase">Announcement</span>
                <span className="h-3 w-[1px] bg-white/30 hidden md:inline-block" />
                <span>
                  {form.message}{" "}
                  {form.linkUrl ? (
                    <span className="underline font-semibold">
                      {form.linkLabel || "Learn more"}
                    </span>
                  ) : null}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("brokers");
  const [brokers, setBrokers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", zip: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");

  const [editForm, setEditForm] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const navigate = useNavigate();

  // ToastContext
  const { addToast } = useToast();
  const notify = (msg, type = "info") => addToast?.(msg, type);
  const notifySuccess = (m) => notify(m, "success");
  const notifyError = (m) => notify(m, "error");

  // listeners refs
  const brokersUnsubRef = useRef(null);
  const logsUnsubRef = useRef(null);
  const testimonialsUnsubRef = useRef(null);

  useEffect(() => {
    const auth = getAuth(app);
    const db = getFirestore(app);
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) { setLoading(false); return; }

      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        const admin = snap.exists() && snap.data().role === "Admin";
        setIsAdmin(admin);

        brokersUnsubRef.current = onSnapshot(
          query(collection(db, "brokers"), orderBy("createdAt", "desc")),
          (s) => setBrokers(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
          () => notifyError("Unable to read brokers.")
        );

        testimonialsUnsubRef.current = onSnapshot(
          query(collection(db, "testimonials_submissions"), orderBy("createdAt", "desc")),
          (s) => setTestimonials(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
          () => notifyError("Unable to read testimonials.")
        );

        if (admin) {
          logsUnsubRef.current = onSnapshot(
            query(collection(db, "logs"), orderBy("timestamp", "desc"), limit(200)),
            (s) => setLogs(s.docs.map((d) => ({ id: d.id, ...d.data() })))
          );
        }
      } finally {
        setLoading(false);
      }
    });
    return () => {
      unsub?.();
      brokersUnsubRef.current?.();
      testimonialsUnsubRef.current?.();
      logsUnsubRef.current?.();
    };
  }, []);

  const logAction = async (action) => {
    try {
      await addDoc(collection(getFirestore(app), "logs"), {
        action, timestamp: serverTimestamp(), by: user?.email || "system",
      });
    } catch {}
  };

  // File handlers
  const onFileChange = (f) => {
    if (!f) { setFile(null); setPreview(""); return; }
    if (!/^image\//.test(f.type)) { notifyError("Please select an image file"); return; }
    if (f.size > 5 * 1024 * 1024) { notifyError("Image must be under 5MB"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onEditFileChange = (f) => {
    if (!f) { setEditFile(null); setEditPreview(editForm?.profilePic || ""); return; }
    if (!/^image\//.test(f.type)) { notifyError("Please select an image file"); return; }
    if (f.size > 5 * 1024 * 1024) { notifyError("Image must be under 5MB"); return; }
    setEditFile(f);
    setEditPreview(URL.createObjectURL(f));
  };

  // Broker helpers
  const resetAddForm = () => {
    setForm({ name: "", email: "", phone: "", zip: "" });
    setFile(null);
    setPreview("");
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Full name is required";
    if (!isValidEmail(form.email)) return "Valid email is required";
    const zip = sanitizeZip(form.zip);
    if (!isValidUSZip(zip)) return "A valid 5-digit US ZIP is required";
    if (!file) return "Please upload a profile picture";
    return "";
  };

  const handleAddBroker = async () => {
    const err = validateForm();
    if (err) { notifyError(err); return; }

    const zip = sanitizeZip(form.zip);
    setAdding(true);
    try {
      let cityState;
      try {
        cityState = await fetchCityState(zip);
      } catch {
        notifyError("ZIP not found.");
        setAdding(false);
        return;
      }

      const fileRef = ref(getStorage(app), `brokers/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const profilePic = await getDownloadURL(fileRef);

      await addDoc(collection(getFirestore(app), "brokers"), {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        zip,
        city: cityState.city,
        state: cityState.state,
        profilePic,
        createdAt: serverTimestamp(),
      });

      await logAction(`Added broker: ${form.name} (${cityState.city}, ${cityState.state})`);
      notifySuccess("Broker added");
      resetAddForm();
    } catch (e) {
      console.error(e);
      notifyError("Failed to add broker.");
    } finally {
      setAdding(false);
    }
  };

  const handleEditZipChange = async (val) => {
    const zip = sanitizeZip(val);
    setEditForm((p) => ({ ...p, zip }));
    if (isValidUSZip(zip)) {
      try {
        const { city, state } = await fetchCityState(zip);
        setEditForm((p) => ({ ...p, city, state }));
      } catch {
        setEditForm((p) => ({ ...p, city: "", state: "" }));
        notifyError("ZIP not found.");
      }
    } else {
      setEditForm((p) => ({ ...p, city: "", state: "" }));
    }
  };

  const handleEdit = async () => {
    if (!editForm) return;
    try {
      const id = editForm.id;
      const payload = { ...editForm };
      delete payload.id;

      if (payload.zip != null) {
        payload.zip = sanitizeZip(payload.zip);
        if (!isValidUSZip(payload.zip)) {
          notifyError("Please enter a valid 5-digit US ZIP");
          return;
        }
        try {
          const { city, state } = await fetchCityState(payload.zip);
          payload.city = city;
          payload.state = state;
        } catch {
          notifyError("ZIP not found.");
          return;
        }
      }

      if (editFile) {
        const fileRef = ref(getStorage(app), `brokers/${Date.now()}_${editFile.name}`);
        await uploadBytes(fileRef, editFile);
        payload.profilePic = await getDownloadURL(fileRef);
      }

      await updateDoc(doc(getFirestore(app), "brokers", id), {
        ...payload,
        updatedAt: serverTimestamp(),
      });

      await logAction(`Edited broker: ${payload.name}`);
      notifySuccess("Broker updated");
      setEditForm(null);
      setEditFile(null);
      setEditPreview("");
    } catch (e) {
      console.error(e);
      notifyError("Update failed.");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(getFirestore(app), "brokers", deleteTarget.id));
      await logAction(`Deleted broker: ${deleteTarget.name}`);
      notifySuccess("Broker deleted");
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      notifyError("Delete failed.");
    }
  };

  // Testimonials
  const handleTestimonialAction = async (id, status, name) => {
    try {
      await updateDoc(doc(getFirestore(app), "testimonials_submissions", id), { status });
      await logAction(`${status === "approved" ? "Approved" : "Rejected"} testimonial by ${name}`);
      if (status === "approved") notifySuccess("Testimonial approved");
      else notifyError("Testimonial rejected");
    } catch (e) {
      console.error(e);
      notifyError("Failed to update testimonial");
    }
  };

  // NEW: Delete testimonial
  const handleDeleteTestimonial = async (id, name) => {
    if (!window.confirm(`Delete testimonial by ${name}? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(getFirestore(app), "testimonials_submissions", id));
      await logAction(`Deleted testimonial by ${name}`);
      notifySuccess("Testimonial deleted");
    } catch (e) {
      console.error(e);
      notifyError("Failed to delete testimonial");
    }
  };

  const filteredBrokers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brokers;
    return brokers.filter((b) =>
      [b.name, b.email, b.phone, b.zip, b.city, b.state].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [brokers, search]);

  if (loading) {
    return <div className={`min-h-screen ${brand.bg} grid place-items-center`}><div className="text-gray-500">Loading admin…</div></div>;
  }
  if (!user || !isAdmin) {
    return (
      <div className={`min-h-screen ${brand.bg} grid place-items-center ${brand.text}`}>
        <div className="text-red-600 font-medium">Access Denied</div>
        <Link to="/admin-login" className="mt-3 text-[#002b5c] underline">Go to login</Link>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${brand.bg} ${brand.text}`}>
      <Helmet><title>Admin Panel • Your Choice Associates</title></Helmet>

      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-[#002b5c] text-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold">Admin Panel</h1>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-white/80">{user?.email}</span>
            <button
              onClick={async () => { await signOut(getAuth(app)); navigate("/admin-login"); }}
              className={`rounded-lg px-3 py-2 ${brand.btnInverted}`}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 grid md:grid-cols-[240px,1fr] gap-6">
        {/* Sidebar */}
        <aside className="hidden md:block sticky top-16 self-start">
          <div className={`${brand.card} p-3`}>
            <SidebarLink active={activeTab === "brokers"} onClick={() => setActiveTab("brokers")} icon={FiUsers}>Brokers</SidebarLink>
            <SidebarLink active={activeTab === "broker-applications"} onClick={() => setActiveTab("broker-applications")} icon={FiUserCheck}>Broker Applications</SidebarLink>
            <SidebarLink active={activeTab === "broker-content"} onClick={() => setActiveTab("broker-content")} icon={FiBookOpen}>Broker Content</SidebarLink>
            <SidebarLink active={activeTab === "applications"} onClick={() => setActiveTab("applications")} icon={FiInbox}>Partner Applications</SidebarLink>
            <SidebarLink active={activeTab === "testimonials"} onClick={() => setActiveTab("testimonials")} icon={FiMessageSquare}>Testimonials</SidebarLink>
            <SidebarLink active={activeTab === "logs"} onClick={() => setActiveTab("logs")} icon={FiActivity}>Activity Logs</SidebarLink>
            <SidebarLink active={activeTab === "settings"} onClick={() => setActiveTab("settings")} icon={FiSettings}>Settings</SidebarLink>
          </div>
        </aside>

        {/* Content */}
        <section>
          {/* Mobile Tabs */}
          <div className="md:hidden flex gap-2 mb-4 flex-wrap">
            {["brokers", "broker-applications", "broker-content", "applications", "testimonials", "logs", "settings"].map((t) => (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                className={`px-3 py-2 rounded-xl text-sm transition ${activeTab === t ? "bg-[#002b5c] text-white" : "bg-white"} border border-gray-200`}
              >
                {t === "broker-applications"
                  ? "Broker Apps"
                  : t === "broker-content"
                  ? "Broker Content"
                  : t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* Settings (Announcement Bar) */}
          {activeTab === "settings" && (
            <div className="space-y-6">
              <AnnouncementBarCard />
            </div>
          )}

          {/* Testimonials */}
          {activeTab === "testimonials" && (
            <div className={`${brand.card} p-5`}>
              <h2 className="text-xl font-semibold mb-2">Testimonials</h2>
              {testimonials.length === 0 ? (
                <div className="text-gray-600">No testimonials submitted yet.</div>
              ) : (
                <ul className="space-y-3">
                  {testimonials.map((t) => (
                    <li key={t.id} className="border rounded-xl p-4 bg-white shadow-sm flex flex-col gap-2">
                      <div className="flex justify-between">
                        <div>
                          <div className="font-medium">{t.name}</div>
                          <div className="text-xs text-gray-500">{t.location || "Unknown"} • {t.email}</div>
                        </div>
                        <div className="text-yellow-500 text-sm">{"★".repeat(t.rating || 0)}</div>
                      </div>

                      <p className="text-gray-700 text-sm whitespace-pre-line">{t.message}</p>

                      <div className="flex gap-2 justify-end items-center">
                        {t.status === "pending" ? (
                          <>
                            <button
                              onClick={() => handleTestimonialAction(t.id, "approved", t.name)}
                              className="px-3 py-1 rounded-lg bg-green-600 text-white text-sm"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleTestimonialAction(t.id, "rejected", t.name)}
                              className="px-3 py-1 rounded-lg bg-yellow-600 text-white text-sm"
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          <span
                            className={`px-2 py-1 text-xs rounded-lg ${
                              t.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}
                          >
                            {t.status}
                          </span>
                        )}

                        <button
                          onClick={() => handleDeleteTestimonial(t.id, t.name)}
                          className="px-3 py-1 rounded-lg bg-[#e31c3d] text-white text-sm"
                          title="Delete testimonial"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Partner Applications */}
          {activeTab === "applications" && (
            <div className={`${brand.card} p-5`}>
              <h2 className="text-xl font-semibold mb-2">Partner Applications</h2>
              <PartnerApplicationsPanel />
            </div>
          )}

          {/* Broker Applications (self-signup approvals) */}
          {activeTab === "broker-applications" && (
            <div className={`${brand.card} p-5`}>
              <h2 className="text-xl font-semibold mb-1">Broker Applications</h2>
              <p className="text-sm text-gray-600 mb-4">
                Review, approve, or reject broker self-signups. Approving here does not add
                them to the public "Brokers" directory below — use the "Add Broker" form for that.
              </p>
              <BrokerApplicationsPanel />
            </div>
          )}

          {/* Broker Content (Tutorials & Resources shown on the broker dashboard) */}
          {activeTab === "broker-content" && (
            <div className={`${brand.card} p-5`}>
              <h2 className="text-xl font-semibold mb-1">Broker Content</h2>
              <p className="text-sm text-gray-600 mb-4">
                Manage the Tutorials and Resources shown to approved brokers on their dashboard.
              </p>
              <BrokerContentPanel />
            </div>
          )}

          {/* Brokers */}
          {activeTab === "brokers" && (
            <div className="space-y-6">
              <div className={`${brand.card} p-5`}>
                <h2 className="text-xl font-semibold mb-2">Add Broker</h2>
                <div className="mt-5 grid lg:grid-cols-[auto,1fr] gap-6">
                  <AvatarUploader
                    file={file}
                    preview={preview}
                    onFileChange={onFileChange}
                    label="Upload Profile Image"
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={brand.input} placeholder="Full Name *" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}/>
                    <input className={brand.input} placeholder="Email *" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}/>
                    <input className={brand.input} placeholder="Phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}/>
                    <input className={brand.input} placeholder="ZIP *" value={form.zip} maxLength={5} onChange={(e) => setForm((p) => ({ ...p, zip: sanitizeZip(e.target.value) }))}/>
                    <div className="sm:col-span-2 flex gap-2 pt-2">
                      <button onClick={handleAddBroker} disabled={adding} className={`rounded-xl px-4 py-2 ${brand.btnPrimary} disabled:opacity-60`}>
                        {adding ? "Adding…" : "Add Broker"}
                      </button>
                      <button onClick={resetAddForm} className={`rounded-xl px-4 py-2 ${brand.btnGhost}`}>Reset</button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className={`${brand.input} sm:max-w-md bg-white`} />
                <div className={brand.chip}>{filteredBrokers.length} result{filteredBrokers.length !== 1 ? "s" : ""}</div>
              </div>
              {filteredBrokers.length === 0 ? (
                <div className={`${brand.card} p-10 text-center text-gray-600`}>No brokers found.</div>
              ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredBrokers.map((b) => (
                    <div key={b.id} className={`${brand.card} p-4 flex items-center gap-4`}>
                      {b.profilePic ? (
                        <img src={b.profilePic} alt={b.name} className="w-12 h-12 rounded-full object-cover border border-gray-200 shrink-0"/>
                      ) : (
                        <div className="w-12 h-12 rounded-full grid place-items-center border border-gray-200 bg-gray-50 text-[11px] text-gray-500 shrink-0">N/A</div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{b.name}</div>
                        <div className="text-xs text-gray-600 whitespace-normal break-all">
                          {b.email} {b.phone ? `• ${b.phone}` : ""} {b.zip ? `• ${b.zip}` : ""} {(b.city && b.state) ? ` • ${b.city}, ${b.state}` : ""}
                        </div>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        <button onClick={() => { setEditForm(b); setEditFile(null); setEditPreview(b.profilePic || ""); }} className="h-9 px-3 text-sm rounded-lg bg-white border hover:bg-gray-50">Edit</button>
                        <button onClick={() => setDeleteTarget(b)} className="h-9 px-3 text-sm rounded-lg bg-[#e31c3d] text-white hover:opacity-95">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Logs */}
          {activeTab === "logs" && (
            <div className={`${brand.card} p-5`}>
              <h2 className="text-xl font-semibold mb-4">Admin Activity Logs</h2>
              {logs.length === 0 ? (
                <div className="text-gray-600">No activity yet.</div>
              ) : (
                <ul className="space-y-2">
                  {logs.map((log) => (
                    <li key={log.id} className="text-sm bg-white border border-gray-200 rounded-xl px-3 py-2 flex items-center justify-between">
                      <span>{log.action}</span>
                      <span className="text-gray-500">{log.timestamp?.toDate?.()?.toLocaleString?.() || "…"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Edit Modal */}
      {editForm && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4">
          <div className={`${brand.card} w-full max-w-xl p-5`}>
            <h3 className="text-lg font-semibold mb-4">Edit Broker</h3>
            <div className="grid lg:grid-cols-[auto,1fr] gap-6">
              <AvatarUploader file={editFile} preview={editPreview} onFileChange={onEditFileChange} label="Change Profile Image"/>
              <div className="space-y-3">
                <input className={brand.input} value={editForm.name || ""} placeholder="Full Name" onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}/>
                <input className={brand.input} value={editForm.email || ""} placeholder="Email" onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}/>
                <input className={brand.input} value={editForm.phone || ""} placeholder="Phone" onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}/>
                <input className={brand.input} value={editForm.zip || ""} placeholder="ZIP" maxLength={5} onChange={(e) => handleEditZipChange(e.target.value)}/>
                {(editForm.city || editForm.state) && (
                  <div className="text-xs text-gray-600">Location: {editForm.city}{editForm.city && editForm.state ? ", " : ""}{editForm.state}</div>
                )}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => { setEditForm(null); setEditFile(null); setEditPreview(""); }} className={`rounded-xl px-4 py-2 ${brand.btnGhost}`}>Cancel</button>
              <button onClick={handleEdit} className={`rounded-xl px-4 py-2 ${brand.btnPrimary}`}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4">
          <div className={`${brand.card} w-full max-w-sm p-5`}>
            <h3 className="text-lg font-semibold mb-2">Confirm Delete</h3>
            <p className="text-sm text-gray-700">Are you sure you want to delete <strong>{deleteTarget.name}</strong>?</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className={`rounded-xl px-4 py-2 ${brand.btnGhost}`}>Cancel</button>
              <button onClick={handleDelete} className="rounded-xl px-4 py-2 bg-[#e31c3d] text-white hover:opacity-95">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}